import React, { useState, useEffect, useCallback, SetStateAction, useRef } from 'react';
import css from './ImageCropper.module.css';
 
import Cropper from 'react-easy-crop';  
import { ProfileData } from '../../../interfaces/Hooks/IProfileData';
import Axios from '../../../utils/axios';
import { useAppContext } from '../../../Context';
import RotateClockwise from '../../Atomos/Icons/EditMode/RotateClockwise';
import RotateAntiClockwise from '../../Atomos/Icons/EditMode/RotateAntiClockwise';
import Folder from '../../Atomos/Icons/EditMode/Folder';
import NewButton from '../../Atomos/Buttons/newButton';
import Plus2 from '../../Atomos/Icons/Plus';
import Pending from '../../Atomos/Icons/Pending';
import Warning from '../../Atomos/Icons/Warning';
import Success from '../../Atomos/Icons/Success';

interface Props {
  imageSrc?: string | null;
  onCrop?: (croppedImage: any) => void;
  onHandleUpdated?: (spinner: boolean) => void;
  user?: ProfileData | null;
  onImageUpload: (url: string) => void;
  draggedPhoto?: File | string | null;
  onHandleUpload: () => void; 
  setPhoto:(photo:File | null)=>void;
  shape: "round" | "rect";
  aspectRatio?: number;
  /** When true, crop area matches the uploaded image aspect ratio (e.g. firm logos). */
  adaptCropToImage?: boolean;
  showDimensions?: boolean;
  hideDimensionInputs?: boolean;
  hideSaveButton?: boolean;
  onRegisterSave?: (save: () => void) => void;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const DEFAULT_FREE_CROP_ASPECT = 16 / 9;


const ImageCropper: React.FC<Props> = ({ imageSrc, onCrop, user,onHandleUpdated ,
  setPhoto, onImageUpload, draggedPhoto, onHandleUpload, shape, aspectRatio = 1, adaptCropToImage = false, showDimensions = false, hideDimensionInputs = false, hideSaveButton = false, onRegisterSave}) => {
  const [photoSrc, setPhotoSrc] = useState<string>('');

  const [errorMesssage, setErrorMessage] = useState<string | null>(null);
  const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 20 MB en bytes
  const {mode} = useAppContext();
  const [status, setStatus] = useState<string>('')
  
const isImageSizeValid = (file: File) => {
  return file.size <= MAX_IMAGE_SIZE;
};

useEffect(() => {
  if (draggedPhoto) {
    setErrorMessage(null);
    
    if (typeof draggedPhoto === 'string') {
      const loadImageFromUrl = async () => {
        try {
          if (draggedPhoto.startsWith('data:')) {
            setPhotoSrc(draggedPhoto);
            setErrorMessage(null); 
            return;
          }
          
          const response = await fetch(draggedPhoto, { mode: 'cors' });
          if (!response.ok) {
            throw new Error('Failed to fetch image');
          }
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onload = (e) => {
            if (typeof e.target?.result === 'string') {
              setPhotoSrc(e.target.result);
              setErrorMessage(null); 
            }
          };
          reader.onerror = () => {
            setErrorMessage('Failed to load image. Please try a different image.');
            setPhotoSrc('');
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          try {
            const testImg = new Image();
            testImg.crossOrigin = 'anonymous';
            testImg.onload = () => {
              setPhotoSrc(draggedPhoto);
              setErrorMessage(null);
            };
            testImg.onerror = () => {
              setErrorMessage('Cannot load image due to CORS restrictions. Please use a different image source.');
              setPhotoSrc('');
            };
            testImg.src = draggedPhoto;
          } catch (testError) {
            setErrorMessage('Cannot load image due to CORS restrictions. Please use a different image source.');
            setPhotoSrc('');
          }
        }
      };
      loadImageFromUrl();
    } else if (draggedPhoto instanceof File) {
      const loadImage = async () => {
        const file = draggedPhoto;
        const reader = new FileReader();

        reader.onload = async (e) => {
          if (typeof e.target?.result === 'string') {
            setPhotoSrc(e.target.result);
            setErrorMessage(null); 
          }
        };
        
        reader.onerror = () => {
          setErrorMessage('Failed to load image file.');
          setPhotoSrc('');
        };

        reader.readAsDataURL(file);
      };

      loadImage();
    }
  } else {
    setPhotoSrc('');
    setErrorMessage(null);
  }
}, [draggedPhoto]);

useEffect(() => {
  if(photoSrc === "" && errorMesssage === null){
    setStatus("")
  }else if(errorMesssage && errorMesssage !== ""){
    setStatus("Warning")
  }else if(photoSrc !== ""){
    setStatus("Success")
  }else{
    setStatus("")
  }
}, [photoSrc, errorMesssage]);

  const [originalImage, setOriginalImage] = useState('');

  const checkImageType = (fileExtension: string | undefined) => {
    const acceptedFileTypes = [".jpg", ".jpeg", ".png", ".gif", ".webp", '.heif', '.bmp', 'tiff', '.dng', 'raw'];
    return acceptedFileTypes.includes(`.${fileExtension?.toLowerCase()}`);
  };
  
  const checkImageSize = (file: File) => {
    return file.size <= MAX_IMAGE_SIZE;
  };
 
  const handleLoadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
  
    const file = event.target.files?.[0];
  
    if (file) {
      try {
        const fileExtension = file.name.split('.').pop();
        setOriginalImage(file.name.toLowerCase());
  
        if (checkImageType(fileExtension) && checkImageSize(file)) {
          const reader = new FileReader();
  
          reader.onload = async () => {
            if (typeof reader.result === 'string') {
              const lowerResImage = await loadImage(reader.result, 800, 800);
              const resizedImageUrl = await resizeImage(
                lowerResImage,
                800,
                800,
                reader.result
              );

              setPhotoSrc(resizedImageUrl);
            }
          };
  
          reader.readAsDataURL(file);
          setErrorMessage('');
        } else {
          setErrorMessage(checkImageType(fileExtension) ? 'Image size exceeds the 25 MB limit.' : 'File type not supported. Accepted file types are: .jpg, .jpeg, .png, .gif, .webp');
        }
      } catch (error) {
      } finally {
        onHandleUpload();
      }
    }
  };

const loadImage = (src: string, maxWidth: number, maxHeight: number) => {
  return new Promise<HTMLImageElement>((resolve) => {
    const img = new Image();
    img.src = src;

    img.onload = () => {
      resolve(img);
    };
  });
};

const mimeFromDataUrl = (dataUrl: string): string | null => {
  if (!dataUrl.startsWith("data:")) {
    return null;
  }
  const semi = dataUrl.indexOf(";");
  const comma = dataUrl.indexOf(",");
  const end = semi !== -1 ? semi : comma !== -1 ? comma : dataUrl.length;
  return dataUrl.slice(5, end).toLowerCase() || null;
};

const shouldExportAsJpeg = (mime: string | null) =>
  mime === "image/jpeg" || mime === "image/jpg";

const resizeImage = (
  image: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
  sourceDataUrl: string
) => {
  return new Promise<string>((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (ctx) {
      const newWidth = Math.min(image.width, maxWidth);
      const newHeight = (newWidth / image.width) * image.height;

      canvas.width = newWidth;
      canvas.height = newHeight;

      ctx.drawImage(image, 0, 0, newWidth, newHeight);

      const mime = mimeFromDataUrl(sourceDataUrl);
      const asJpeg = shouldExportAsJpeg(mime);
      const resizedImageUrl = asJpeg
        ? canvas.toDataURL("image/jpeg", 0.92)
        : canvas.toDataURL("image/png");
      resolve(resizedImageUrl);
    }
  });
};

const dataURItoBlob = (dataURI: string) => {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
}




const [rotation, setRotation] = useState<number>(0);

const handleRotation = (direction:string) => {
  if(direction === 'clockwise') {
    setRotation(rotation + 90)
  }
  else if(direction === 'anticlockwise') {
    setRotation(rotation - 90)
  }
}
 
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const cropperContainerRef = useRef<HTMLDivElement>(null);
  const [cropDimensions, setCropDimensions] = useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [desiredDimensions, setDesiredDimensions] = useState({ width: '', height: '' });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [calculatedAspectRatio, setCalculatedAspectRatio] = useState<number | undefined>(showDimensions ? undefined : aspectRatio);
  const [imageNaturalAspect, setImageNaturalAspect] = useState<number | undefined>(undefined);
  const [isEditingDimensions, setIsEditingDimensions] = useState({ width: false, height: false });
  const [manualDimensionsActive, setManualDimensionsActive] = useState(false);

  const getFreeCropAspect = useCallback(() => {
    if (containerSize.width > 0 && containerSize.height > 0) {
      return containerSize.width / containerSize.height;
    }
    return DEFAULT_FREE_CROP_ASPECT;
  }, [containerSize.width, containerSize.height]);

  const activeAspect = adaptCropToImage && imageNaturalAspect
    ? imageNaturalAspect
    : showDimensions
      ? manualDimensionsActive && calculatedAspectRatio != null
        ? calculatedAspectRatio
        : getFreeCropAspect()
      : aspectRatio;

  const shouldUseDesiredDimensions =
    manualDimensionsActive ||
    isEditingDimensions.width ||
    isEditingDimensions.height;

  const resetCropInteractionState = useCallback(() => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setRotation(0);
    setManualDimensionsActive(false);
    setDesiredDimensions({ width: "", height: "" });
    setIsEditingDimensions({ width: false, height: false });
    setCropDimensions({ width: 0, height: 0 });
    setImageNaturalAspect(undefined);
    setCalculatedAspectRatio(showDimensions ? undefined : aspectRatio);
  }, [aspectRatio, showDimensions]);

  useEffect(() => {
    if (!photoSrc) {
      return;
    }
    resetCropInteractionState();
  }, [photoSrc, resetCropInteractionState]);
  
  const handleOnSave = useCallback(() => {
    setPhoto(croppedImage);
    onImageUpload(croppedImage?.name as string);
  }, [croppedImage, onImageUpload, setPhoto]);

  useEffect(() => {
    onRegisterSave?.(handleOnSave);
  }, [handleOnSave, onRegisterSave]);

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    if (showDimensions && croppedAreaPixels.width && croppedAreaPixels.height) {
      const newDimensions = {
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height)
      };
      setCropDimensions(newDimensions);
      
      if (!shouldUseDesiredDimensions) {
        setDesiredDimensions({
          width: newDimensions.width.toString(),
          height: newDimensions.height.toString()
        });
      }
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
  
    if (ctx && croppedAreaPixels.width && croppedAreaPixels.height) {
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
  
      const image = new Image();
      if (photoSrc.startsWith('http://') || photoSrc.startsWith('https://')) {
        image.crossOrigin = 'anonymous';
      }
      image.src = photoSrc;

      image.onload = () => {
        const rotatedCanvas = document.createElement("canvas");
        const rotatedCtx = rotatedCanvas.getContext("2d");
  
        const rotationInRadians = (rotation * Math.PI) / 180;
  
        rotatedCanvas.width = image.width;
        rotatedCanvas.height = image.height;
        rotatedCtx?.translate(image.width / 2, image.height / 2);
        rotatedCtx?.rotate(rotationInRadians);
        rotatedCtx?.drawImage(image, -image.width / 2, -image.height / 2);
  
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;
        ctx.drawImage(
          rotatedCanvas,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        const sourceMime = mimeFromDataUrl(photoSrc);
        const asJpeg = shouldExportAsJpeg(sourceMime);
        const croppedImageDataURL = asJpeg
          ? canvas.toDataURL("image/jpeg", 0.92)
          : canvas.toDataURL("image/png");
        const croppedImageBlob = dataURItoBlob(croppedImageDataURL);
        let fileName = originalImage;
        if (!fileName?.trim()) {
          fileName = asJpeg ? "image.jpg" : "image.png";
        } else if (asJpeg && !/\.(jpe?g)$/i.test(fileName)) {
          fileName = fileName.replace(/\.[^.]+$/, "") + ".jpg";
        } else if (!asJpeg && !/\.(png)$/i.test(fileName)) {
          fileName = fileName.replace(/\.[^.]+$/, "") + ".png";
        }
        const file = new File([croppedImageBlob], fileName, { type: croppedImageBlob.type });
  
        setCroppedImage(file);  
      };
    }

 
  };

  const [base64Image, setBase64Image] = useState('');
 useEffect(() => {
    const rutaRelativa = '/assets/images&icons/DefaultImageCropper.png';

    const urlActual = window.location.origin;

    const urlCompleta = `${urlActual}${rutaRelativa}`;

    fetch(urlCompleta)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64data = reader.result;
          setBase64Image(base64data as string);
        };
        reader.readAsDataURL(blob);
      });
  }, []);

  const getContainerDimensions = () => {
    if (adaptCropToImage && imageNaturalAspect) {
      const maxW = 480;
      const maxH = 360;
      let width = maxW;
      let height = width / imageNaturalAspect;
      if (height > maxH) {
        height = maxH;
        width = height * imageNaturalAspect;
      }
      const roundedWidth = Math.round(width);
      const roundedHeight = Math.round(height);
      return {
        width: `${roundedWidth}px`,
        height: `${roundedHeight}px`,
        minWidth: `${roundedWidth}px`,
        minHeight: `${roundedHeight}px`,
        maxWidth: "100%",
      };
    }

    if (showDimensions) {
      return {
        minWidth: "300px",
        minHeight: "240px",
        width: "100%",
        maxWidth: "560px",
        height: "240px",
      };
    }

    if (aspectRatio && aspectRatio !== 1) {
      const baseSizePortrait = 220;
      const baseSizeLandscape = 300;
      
      if (aspectRatio < 1) {
        const minHeight = baseSizePortrait;
        const calculatedWidth = minHeight * aspectRatio;
        const finalWidth = Math.max(160, calculatedWidth);
        return {
          width: `${finalWidth}px`,
          height: `${minHeight}px`,
          minWidth: `${finalWidth}px`,
          minHeight: `${minHeight}px`,
          maxWidth: '550px',
          aspectRatio: `${aspectRatio}`,
        };
      } else {
        const minWidth = baseSizeLandscape;
        const calculatedHeight = minWidth / aspectRatio;
        const finalHeight = Math.max(160, calculatedHeight);
        return {
          width: `${minWidth}px`,
          height: `${finalHeight}px`,
          minWidth: `${minWidth}px`,
          minHeight: `${finalHeight}px`,
          maxWidth: '550px',
          aspectRatio: `${aspectRatio}`,
        };
      }
    }
    return {
      minWidth: '280px',
      minHeight: '280px',
      width: '100%',
      maxWidth: '450px',
    };
  };

  useEffect(() => {
    if (cropperContainerRef.current) {
      const updateSize = () => {
        if (cropperContainerRef.current) {
          setContainerSize({
            width: cropperContainerRef.current.clientWidth,
            height: cropperContainerRef.current.clientHeight
          });
        }
      };
      updateSize();
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(cropperContainerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [photoSrc, aspectRatio, showDimensions, adaptCropToImage, imageNaturalAspect]);

  const applyDesiredDimensions = (desiredWidth: number, desiredHeight: number) => {
    if (desiredWidth > 0 && desiredHeight > 0) {
      const newAspectRatio = desiredWidth / desiredHeight;
      setCalculatedAspectRatio(newAspectRatio);
      setManualDimensionsActive(true);
      setCrop({ x: 0, y: 0 });
    }
  };

  const handleDimensionChange = (type: 'width' | 'height', value: string) => {
    if (type === 'width') {
      setDesiredDimensions(prev => ({ ...prev, width: value }));
    } else {
      setDesiredDimensions(prev => ({ ...prev, height: value }));
    }
  };

  const applyAspectRatioFromInputs = () => {
    const width = desiredDimensions.width ? parseInt(desiredDimensions.width, 10) : 0;
    const height = desiredDimensions.height ? parseInt(desiredDimensions.height, 10) : 0;
    
    if (width > 0 && height > 0) {
      applyDesiredDimensions(width, height);
    } else {
      setManualDimensionsActive(false);
      setCalculatedAspectRatio(undefined);
    }
  };

  const handleMediaLoaded = useCallback((mediaSize: { naturalWidth: number; naturalHeight: number }) => {
    setImageSize({
      width: mediaSize.naturalWidth,
      height: mediaSize.naturalHeight,
    });
    if (
      adaptCropToImage &&
      mediaSize.naturalWidth > 0 &&
      mediaSize.naturalHeight > 0
    ) {
      setImageNaturalAspect(mediaSize.naturalWidth / mediaSize.naturalHeight);
    }
  }, [adaptCropToImage]);

  const handleCropInteractionStart = useCallback(() => {
    if (!showDimensions || hideDimensionInputs) {
      return;
    }
    setManualDimensionsActive(false);
    setCalculatedAspectRatio(undefined);
    setIsEditingDimensions({ width: false, height: false });
  }, [hideDimensionInputs, showDimensions]);

  useEffect(() => {
    if (!showDimensions) {
      setCalculatedAspectRatio(aspectRatio);
      return;
    }

    if (!manualDimensionsActive) {
      setCalculatedAspectRatio(undefined);
    }
  }, [aspectRatio, manualDimensionsActive, showDimensions]);

  useEffect(() => {
    const container = cropperContainerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      
      const modalContent = container.closest('.modal-content') as HTMLElement;
      if (modalContent) {
        const scrollTop = modalContent.scrollTop;
        const scrollHeight = modalContent.scrollHeight;
        const clientHeight = modalContent.clientHeight;
        const deltaY = e.deltaY;
        
        const canScrollUp = scrollTop > 0;
        const canScrollDown = scrollTop + clientHeight < scrollHeight;
        
        if ((deltaY > 0 && canScrollDown) || (deltaY < 0 && canScrollUp)) {
          modalContent.scrollTop += deltaY;
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    
    return () => {
      container.removeEventListener('wheel', handleWheel, { capture: true } as EventListenerOptions);
    };
  }, []);

  return (
    <div className={`z-30 relative flex flex-col gap-3 md:gap-4 `}>
      <div className="bg-gray-font md:px-6">
        <div 
          ref={cropperContainerRef}
          className="m-auto z-20 relative"
          style={getContainerDimensions()}
        >
          <Cropper
            image={photoSrc ? photoSrc : ","}
            crop={crop}
            zoom={zoom}
            aspect={activeAspect}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            classes={{ containerClassName: css.CropArea }}
            onCropComplete={onCropComplete}
            onMediaLoaded={handleMediaLoaded}
            onInteractionStart={handleCropInteractionStart}
            rotation={rotation}
            objectFit={showDimensions || adaptCropToImage ? "contain" : "horizontal-cover"}
            cropShape={shape}
          />
        </div>
      </div>
      <div className={`${css.photoOptions}`}>
        <div className={`${css.optionButtons}`}></div>
        {showDimensions && !hideDimensionInputs && (
          <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <p className="text-sm font-medium text-gray-font mb-3">Crop Dimensions:</p>
            <div className="flex flex-col gap-3">
              <div className="flex gap-4 items-center flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Width:</label>
                  {cropDimensions.width > 0 ? (
                    <input
                      type="number"
                      value={
                        shouldUseDesiredDimensions
                          ? desiredDimensions.width || cropDimensions.width.toString()
                          : cropDimensions.width
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setIsEditingDimensions(prev => ({ ...prev, width: true }));
                        handleDimensionChange('width', value);
                        const otherH =
                          desiredDimensions.height || cropDimensions.height.toString();
                        if (value === '' || !otherH) {
                          setCalculatedAspectRatio(undefined);
                        }
                      }}
                      onFocus={() => {
                        setIsEditingDimensions(prev => ({ ...prev, width: true }));
                        setDesiredDimensions(prev => ({
                          width: prev.width || cropDimensions.width.toString(),
                          height: prev.height || cropDimensions.height.toString()
                        }));
                      }}
                      onBlur={() => {
                        setIsEditingDimensions(prev => ({ ...prev, width: false }));
                        const width = desiredDimensions.width ? parseInt(desiredDimensions.width) : 0;
                        const height = desiredDimensions.height ? parseInt(desiredDimensions.height) : 0;
                        if (width > 0 && height > 0) {
                          applyAspectRatioFromInputs();
                        } else {
                          setDesiredDimensions({
                            width: cropDimensions.width.toString(),
                            height: cropDimensions.height.toString()
                          });
                        }
                      }}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-gray-font"
                      min="1"
                      max={imageSize.width || 10000}
                    />
                  ) : (
                    <span className="text-base font-semibold text-gray-font">-</span>
                  )}
                  <span className="text-sm text-gray-font">px</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Height:</label>
                  {cropDimensions.height > 0 ? (
                    <input
                      type="number"
                      value={
                        shouldUseDesiredDimensions
                          ? desiredDimensions.height || cropDimensions.height.toString()
                          : cropDimensions.height
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setIsEditingDimensions(prev => ({ ...prev, height: true }));
                        handleDimensionChange('height', value);
                        const otherW =
                          desiredDimensions.width || cropDimensions.width.toString();
                        if (value === '' || !otherW) {
                          setCalculatedAspectRatio(undefined);
                        }
                      }}
                      onFocus={() => {
                        setIsEditingDimensions(prev => ({ ...prev, height: true }));
                        setDesiredDimensions(prev => ({
                          width: prev.width || cropDimensions.width.toString(),
                          height: prev.height || cropDimensions.height.toString()
                        }));
                      }}
                      onBlur={() => {
                        setIsEditingDimensions(prev => ({ ...prev, height: false }));
                        const width = desiredDimensions.width ? parseInt(desiredDimensions.width) : 0;
                        const height = desiredDimensions.height ? parseInt(desiredDimensions.height) : 0;
                        if (width > 0 && height > 0) {
                          applyAspectRatioFromInputs();
                        } else {
                          setDesiredDimensions({
                            width: cropDimensions.width.toString(),
                            height: cropDimensions.height.toString()
                          });
                        }
                      }}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-gray-font"
                      min="1"
                      max={imageSize.height || 10000}
                    />
                  ) : (
                    <span className="text-base font-semibold text-gray-font">-</span>
                  )}
                  <span className="text-sm text-gray-font">px</span>
                </div>
                {imageSize.width > 0 && imageSize.height > 0 && (
                  <div className="ml-auto text-xs text-gray-font">
                    Original: {imageSize.width} × {imageSize.height}px
                  </div>
                )}
              </div>
              {cropDimensions.width > 0 && cropDimensions.height > 0 && (
                <div className="text-xs text-gray-font">
                  Current crop: {cropDimensions.width} × {cropDimensions.height}px
                </div>
              )}
            </div>
          </div>
        )}
        <fieldset className="flex justify-between items-center gap-4 relative">
          <div className="w-full relative flex flex-col gap-2">
            <legend className=" text-xl lg:text-[1.375rem] not-italic font-bold leading-6 text-gray-font">
              Crop
            </legend>
            <div className={`flex w-full flex-col md:flex-row gap-2.5 justify-center md:justify-between items-center`}>
              <div className="w-full flex flex-col gap-1">
                <p className=" text-base lg:text-lg not-italic font-normal text-gray-font leading-8">
                  Zoom
                </p>
                <div className="flex gap-2 w-full ">
                  <span className="text-neutral-dark text-[.75rem]">-</span>
                  <input
                    type="range"
                    id={``}
                    min={MIN_ZOOM}
                    max={MAX_ZOOM}
                    step={0.05}
                    aria-spanledby="Zoom"
                    onChange={(e: any) => {
                      setZoom(Number(e.target.value));
                    }}
                    value={zoom}
                    className={`${
                      mode === "member" ? css.scaleSlider : css.scaleSliderStaff
                    } w-8/12 custom-range appearance-none  h-2 bg-gray-300 rounded-md`}
                  />
                  <span className="text-neutral-dark text-[.75rem]">+</span>
                </div>
              </div>
              <div>
                <div
                  className={`  ${css.optionButtons} lg:justify-end justify-center lg:mt-3 md:mt-0 flex lg:gap-2 items-center text-neutral-dark`}
                >
                  <button
                    id={`${css.rotateClockwise}`}
                    onClick={() => handleRotation("clockwise")}
                    type="button"
                  >
                    <RotateClockwise className="w-5 h-5 fill-gray-font"/>
                  </button>
                  <button
                    id={`${css.rotateCounterClockwise}`}
                    onClick={() => handleRotation("anticlockwise")}
                    type="button"
                  >
                    <RotateAntiClockwise className="w-5 h-5 fill-gray-font"/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
      <input
        type="file"
        id="fileInput"
        style={{ display: "none" }}
        accept="*"
        onChange={handleLoadImage}
        className="z-20 relative"
      />
      {errorMesssage && (
        <div className="text-feedback-error text-base lg:text-lg text-left relative bottom-3">
          {errorMesssage}
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <span
            className={`text-base lg:text-lg font-normal leading-6 text-left text-gray-font `}
          >
            Accepted file types are: .jpg, .jpeg, .png .gif and .webP.
          </span>
          <span
            className={`text-base lg:text-lg font-normal leading-6 text-left text-gray-font`}
          >
            Up to 25 MB.
          </span>
        </div>
        
        <div className={`flex gap-3 ${hideSaveButton ? "justify-start" : "md:justify-between"}`}>
          <label
            className={` flex items-center py-2 lg:py-3 pr-2 pl-3 gap-2 lg:gap-3 border border-s border-gray-font text-gray-font text-base lg:text-lg font-decimal cursor-pointer`}
            htmlFor="fileInput"
            id="customFileInput"
          >
            SELECT FILE
            <Folder className="w-5 h-5 lg:w-6 lg:h-6 fill-gray-font"/>
          </label>
          
          {!hideSaveButton ? (
            <div className='!min-w-[120px]'>
              <NewButton
                onClick={handleOnSave}
                text={"ADD"}
                icon={<Plus2 />}
                color={mode === "member" ? "primary" : "accent"}
                content="textIcon"
                size="medium"
                style="filled"
              />
            </div>
          ) : null}
        </div>

          {status === "" &&
            <div className='flex items-center gap-2'>
              <Pending className="w-5 h-5 lg:w-6 lg:h-6  fill-gray-font"/>
              <span className="text-base lg:text-lg leading-8 font-normal not-italic text-gray-font">
                Please select a file.
              </span>
            </div>
          }

          {status === "Warning" &&
            <div className='flex items-center gap-2'>
              <Warning className="w-5 h-5 lg:w-6 lg:h-6 fill-accentDefault"/>
              <span className="text-base lg:text-lg leading-8 font-normal not-italic text-accentDefault">
                Upload Failed.
              </span>
            </div>
          }

          {status === "Success" &&
            <div className='flex items-center gap-2'>
              <Success className="w-5 h-5 lg:w-6 lg:h-6 fill-feedback-success-Default"/>
              <span className="text-base lg:text-lg leading-8 font-normal not-italic text-feedback-success-Default">
                Upload Successful.
              </span>
            </div>
          }

      </div>
    </div>
  );  
};

export default ImageCropper;